import {
    Button,
    Col,
    Modal,
    notification,
    Row,
    Space,
    Switch,
    Typography,
} from 'antd';
import { useContext, useState } from 'react';
import { AiOutlineCopy } from 'react-icons/ai';
import { editWorkspace } from '../../../../api/workspaces';
import { WorkspaceDetailsContext } from '../../../../screens/WorkspaceDetails/context/WorkspaceDetailsContext';
import { copyText } from '../../../../utils/general';
import CardDataBase from '../../../CardDataBase';
import ChangeConfig from './ChangeConfig';

const { Text } = Typography;

interface Props {
    workspaceId: string;
    masterKey: string;
    requiresProofOfDelivery: boolean;
}

const SettingsDetail = ({
    workspaceId,
    masterKey,
    requiresProofOfDelivery,
}: Props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [proofActive, setProofActive] = useState(requiresProofOfDelivery);

    const { workspace, setWorkspace } = useContext(WorkspaceDetailsContext);

    const handleProofChange = async (value: boolean) => {
        setLoading(true);
        editWorkspace(workspaceId, { requiresProofOfDelivery: value })
            .then(() => setProofActive(value))
            .catch((error) => notification.error({
                message: 'Error',
                description: error || error.message || 'An error occurred',
            }))
            .finally(() => setLoading(false));
    };

    const handleLiteProviderChange = (showLiteProvider: boolean) => Modal.confirm({
        title: 'Edit Lite Provider Screen',
        content: `Are you sure you want to ${showLiteProvider ? 'show' : 'hide'} the Lite Provider Screen?`,
        onOk: async () => {
            try {
                const { data } = await editWorkspace(workspaceId, { showLiteProvider });
                setWorkspace(data.data);
                notification.success({
                    message: 'Success',
                    description: 'Lite Provider Screen changed successfully',
                });
            } catch (error) {
                console.log(error);
                notification.error({
                    message: 'Error',
                    description: 'Something went wrong, please try again later',
                });
            }
        },
        okText: 'Change',
        cancelText: 'Cancel',
    });

    const CARD_CONTENT = [
        {
            title: 'Master Key',
            content: (
                <Space>
                    {masterKey}
                    <Button
                        type="link"
                        size="small"
                        icon={(
                            <AiOutlineCopy
                                style={{ verticalAlign: 'middle' }}
                                size={18}
                            />
                        )}
                        onClick={() => copyText({ value: masterKey, messageSuccess: 'Master key copied!' })}
                        style={{ padding: 0 }}
                    />
                </Space>
            ),
        },
        {
            title: 'Proof of Delivery',
            content: (
                <Switch
                    checked={proofActive}
                    onChange={handleProofChange}
                    loading={loading}
                />
            ),
        },
        {
            title: 'Lite provider screen',
            content: (
                <Switch
                    checked={workspace?.showLiteProvider}
                    onChange={handleLiteProviderChange}
                />
            ),
        },
        {
            title: 'Driver phone certification',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.isPhoneCertificationEnabled}
                    configName="Driver Phone Certification"
                    varName="isPhoneCertificationEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Link to Pay',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.linkToPayEnabled}
                    configName="Link to Pay"
                    varName="linkToPayEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Bookings with tip',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.bookingsWithTipEnabled}
                    configName="Create Booking with tip"
                    varName="bookingsWithTipEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Cash in store',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.cashInStoreEnabled}
                    configName="Cash in store"
                    varName="cashInStoreEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Flows',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.builderEnabled}
                    configName="Flows"
                    varName="builderEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'My Fleet Metrics Section',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.myFleetMetricsSectionEnabled}
                    configName="My Fleet Metrics Section"
                    varName="myFleetMetricsSectionEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: '4 Wheels',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.carTypeEnabled}
                    configName="4 Wheels"
                    varName="carTypeEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Customers Autofill',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.customersAutoFillEnabled}
                    configName="Customers Autofill"
                    varName="customersAutoFillEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Bank Data Validation',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.bankDataValidationEnabled}
                    configName="Bank Data Validation"
                    varName="bankDataValidationEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
        {
            title: 'Show Taxes',
            content: (
                <ChangeConfig
                    checked={workspace?.workspaceSettings?.taxesEnabled}
                    configName="Show Taxes"
                    varName="taxesEnabled"
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                />
            ),
        },
    ];

    return (
        <CardDataBase title="Settings" style={{ minHeight: 345 }}>
            {
                CARD_CONTENT.map((data) => (
                    <Row align="middle" justify="space-between" style={{ margin: '16px 0' }}>
                        <Col>
                            <Text strong>
                                {data.title}
                                :
                            </Text>
                        </Col>
                        <Col>{data.content}</Col>
                    </Row>
                ))
            }
        </CardDataBase>
    );
};

export default SettingsDetail;
